// Configurations and Utilities
const theme = {
  config: {
    bpSmall: false,
    hasSessionStorage: true,
    hasLocalStorage: true,
    mediaQuerySmall: 'screen and (max-width: 769px)',
    youTubeReady: false,
    vimeoReady: false,
    vimeoLoading: false,
    isTouch: 'ontouchstart' in window || window.navigator.maxTouchPoints > 0,
    stickyHeader: false,
    rtl: document.documentElement.getAttribute('dir') === 'rtl',
  },
  utils: {
    defaultTo: (value, defaultValue) => (value == null || value !== value ? defaultValue : value),

    debounce: (wait, callback) => {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => callback.apply(this, args), wait);
      };
    },

    throttle: (limit, callback) => {
      let waiting = false;
      return (...args) => {
        if (!waiting) {
          callback.apply(this, args);
          waiting = true;
          setTimeout(() => (waiting = false), limit);
        }
      };
    },
  },
};

// Initialize AOS animations
function initAOSAnimations() {
  if (window.AOS) {
    window.AOS.init({
      duration: 800,
      easing: 'ease-in-out',
      once: true,
    });
  }
}

// Slideshow Constructor
class Slideshow {
  constructor(el, options = {}) {
    this.el = el;
    this.options = { ...Slideshow.defaults, ...options };

    this.init();
  }

  static defaults = {
    autoPlay: false,
    fade: false,
    wrapAround: true,
    draggable: true,
    pauseAutoPlayOnHover: true,
    autoPlaySpeed: 5000,
  };

  init() {
    this.slideshow = new Flickity(this.el, this.options);
    this.setupListeners();
    this.addLoadedClass();
    initAOSAnimations();
  }

  setupListeners() {
    if (this.options.autoPlay) {
      const pauseBtn = this.el.closest('.slideshow-wrapper').querySelector('.slideshow__pause');
      pauseBtn?.addEventListener('click', () => this.togglePlayPause(pauseBtn));
    }
  }

  togglePlayPause(pauseBtn) {
    if (pauseBtn.classList.contains('is-paused')) {
      pauseBtn.classList.remove('is-paused');
      this.slideshow.playPlayer();
    } else {
      pauseBtn.classList.add('is-paused');
      this.slideshow.pausePlayer();
    }
  }

  addLoadedClass() {
    const parentContainer = this.el.closest('.slideshow-wrapper');
    if (parentContainer) {
      parentContainer.classList.add('loaded');
    }
  }

  destroy() {
    this.slideshow.destroy();
  }
}

// Slideshow Section Management
class SlideshowSection {
  constructor(container) {
    this.container = container;
    const sectionId = container.dataset.sectionId;
    this.slideshowEl = container.querySelector(`#Slideshow-${sectionId}`);

    if (this.slideshowEl) {
      this.initSlideshow();
    }
  }

  initSlideshow() {
    let slides = this.slideshowEl.querySelectorAll('.slideshow__slide');
    const options = {
      prevNextButtons: this.slideshowEl.hasAttribute('data-arrows'),
      pageDots: this.slideshowEl.hasAttribute('data-dots'),
      fade: true,
      autoPlay: this.slideshowEl.dataset.autoplay === 'true' ? parseInt(this.slideshowEl.dataset.speed) : false,
      draggable: slides.length > 1
    };
    this.slideshow = new Slideshow(this.slideshowEl, options);
  }

  destroy() {
    this.slideshow?.destroy();
  }
}

// Initialize Sections
document.addEventListener('DOMContentLoaded', () => {
  initAOSAnimations();
  const sections = document.querySelectorAll('[data-section-type="slideshow-section"]');
  sections.forEach((section) => new SlideshowSection(section));
});


// parallax  image class
class ParallaxImage extends HTMLElement {
  constructor() {
    super();
    this.parallaxImage = this.querySelector('[data-parallax-image]');
    this.windowInnerHeight = window.innerHeight;
    this.isActive = false;
    this.timeout = null;
    this.directionMap = {
      right: 0,
      top: 90,
      left: 180,
      bottom: 270
    }
    this.directionMultipliers = {
      0: [  1,  0 ],
      90: [  0, -1 ],
      180: [ -1,  0 ],
      270: [  0,  1 ]
    }

    this.init();
    window.addEventListener('scroll', () => this.scrollHandler());
  }

  getParallaxInfo() {
    const { width, height, top } = this.parallaxImage.getBoundingClientRect();
    let element = this.parallaxImage;
    let multipliers;
    let { angle, movement } = element.dataset;

    let movementPixels = angle === 'top' ? Math.ceil(height * (parseFloat(movement) / 100)) : Math.ceil(width * (parseFloat(movement) / 100));

    // angle has shorthands "top", "left", "bottom" and "right"
    // nullish coalescing. using `||` here would fail for `0`
    angle = this.directionMap[angle] ?? parseFloat(angle);

    // fallback if undefined
    // NaN is the only value that doesn't equal itself
    if (angle !== angle) angle = 270; // move to bottom (default parallax effect)
    if (movementPixels !== movementPixels) movementPixels = 100; // 100px

    // check if angle is located in top half and/or left half
    angle %= 360;
    if (angle < 0) angle += 360

    const toLeft = angle > 90 && angle < 270;
    const toTop  = angle < 180;

    element.style[toLeft ? 'left' : 'right'] = 0;
    element.style[toTop  ? 'top' : 'bottom'] = 0;

    // if it's not a perfectly horizontal or vertical movement, get cos and sin
    if (angle % 90) {
      const radians = angle * Math.PI / 180
      multipliers = [ Math.cos(radians), Math.sin(radians) * -1 ] // only sin has to be inverted
    } else {
      multipliers = this.directionMultipliers[angle];
    }

    // increase width and height according to movement and multipliers
    if (multipliers[0]) element.style.width  = `calc(100% + ${movementPixels * Math.abs(multipliers[0])}px)`;
    if (multipliers[1]) element.style.height = `calc(100% + ${movementPixels * Math.abs(multipliers[1])}px)`;

    return {
      element,
      movementPixels,
      multipliers,
      top,
      height
    }
  }

  init() {
    const { element, movementPixels, multipliers, top, height } = this.getParallaxInfo();;

    const scrolledInContainer = this.windowInnerHeight - top;
    const scrollArea = this.windowInnerHeight + height;
    const progress = scrolledInContainer / scrollArea;

    if (progress > -0.1 && progress < 1.1) {
      const position = Math.min(Math.max(progress, 0), 1) * movementPixels;
      element.style.transform = `translate3d(${position * multipliers[0]}px, ${position * multipliers[1]}px, 0)`;
    }

    if (this.isActive) requestAnimationFrame(this.init.bind(this));
  }

  scrollHandler() {
    if (this.isActive) {
      clearTimeout(this.timeout);
    } else {
      this.isActive = true;
      requestAnimationFrame(this.init.bind(this));
    }

    this.timeout = setTimeout(() => this.isActive = false, 20);
  }
}

customElements.define('parallax-image', ParallaxImage);